import React, { useState, useEffect, useMemo } from "react";
import { Table, Space, Col, Tag, message, Tooltip, Typography } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    ReloadOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    ArrowUpOutlined
} from "@ant-design/icons";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";
import { FilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import ProductInventoryThresholdServ from "../../service/ProductInventoryThresholdServ";
import AddProductThreshold from "../component/AddProductThreshold";
import TableImage from "../../component/ui/TableImage";
import dayjs from "dayjs";
import { useAdminTable } from "../hooks/useAdminTable";

const { Text } = Typography;

const ProductInventoryThresholds = () => {
    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedThreshold, setSelectedThreshold] = useState(null);

    const fetchThresholds = async (params) => {
        const apiParams = {
            limit: params.limit,
            skip: (params.page - 1) * params.limit,
            searchText: params.search,
            sort: JSON.stringify({ updatedAt: -1 })
        };
        const response = await ProductInventoryThresholdServ.index(apiParams);
        return { data: response?.data || [], total: response?.total || 0 };
    };

    const {
        data: thresholds,
        total,
        loading,
        params,
        setParams,
        handleSearch,
        refresh
    } = useAdminTable(fetchThresholds, "admin_product_inventory_thresholds", { page: 1, limit: 10 });

    const handleAdd = () => {
        setIsEditing(false);
        setSelectedThreshold(null);
        setModalOpen(true);
    };

    const handleEdit = (record) => {
        setIsEditing(true);
        setSelectedThreshold(record);
        setModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            await ProductInventoryThresholdServ.delete(id);
            message.success("Threshold deleted successfully");
            refresh();
        } catch (error) {
            console.error(error);
            message.error("Failed to delete threshold");
        }
    };

    const columns = [
        {
            title: "Product",
            dataIndex: "product",
            key: "product",
            render: (product) => (
                <div className="pcu-flex pcu-flex-center-y pcu-gap-12">
                    <TableImage src={product?.imageUrl} />
                    <div>
                        <div className="pcu-font-bold pcu-color-text-header">{product?.name || "N/A"}</div>
                        <div className="pcu-font-12 pcu-color-muted">{product?.category || "No Category"}</div>
                    </div>
                </div>
            )
        },
        {
            title: "Thresholds",
            key: "thresholds",
            render: (_, record) => (
                <div className="pcu-flex pcu-gap-16">
                    <div>
                        <div className="pcu-font-10 pcu-color-muted pcu-font-800">MIN</div>
                        <div className="pcu-font-14 pcu-font-bold">{record.minStock}</div>
                    </div>
                    <div style={{ width: 1, background: '#f1f5f9' }} />
                    <div>
                        <div className="pcu-font-10 pcu-color-muted pcu-font-800">MAX</div>
                        <div className="pcu-font-14 pcu-font-bold">{record.maxStock}</div>
                    </div>
                </div>
            )
        },
        {
            title: "Current Stock",
            dataIndex: "currentStock",
            key: "currentStock",
            render: (stock, record) => {
                const isLow = stock <= record.minStock;
                const isOver = stock >= record.maxStock;

                return (
                    <div className="pcu-flex pcu-flex-column">
                        <span className={`pcu-font-18 pcu-font-800 ${isLow ? 'pcu-color-error' : isOver ? 'pcu-color-orange' : 'pcu-color-success'}`}>
                            {stock}
                        </span>
                        {isLow && <span className="pcu-font-10 pcu-color-error pcu-font-bold">LOW STOCK</span>}
                        {isOver && <span className="pcu-font-10 pcu-color-orange pcu-font-bold">OVER STOCK</span>}
                    </div>
                );
            }
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                let color = "success";
                let icon = <CheckCircleOutlined />;
                let label = "Normal";

                if (status === 'LOW') {
                    color = "error";
                    icon = <WarningOutlined />;
                    label = "Low Stock";
                } else if (status === 'OVER') {
                    color = "warning";
                    icon = <ArrowUpOutlined />;
                    label = "Overstocked";
                }

                return (
                    <Tag icon={icon} color={color} className="pcu-radius-6 pcu-padding-x-8 pcu-font-12 pcu-font-600">
                        {label.toUpperCase()}
                    </Tag>
                );
            }
        },
        {
            title: "Last Updated",
            dataIndex: "updatedAt",
            key: "updatedAt",
            render: (date) => (
                <div className="pcu-font-12 pcu-color-muted">
                    {dayjs(date).format("MMM DD, YYYY")}<br />
                    {dayjs(date).format("hh:mm A")}
                </div>
            )
        },
        // {
        //     title: "Actions",
        //     key: "actions",
        //     render: (_, record) => (
        //         <Space>
        //             <Button icon={<EditOutlined />}>
        //                 Edit
        //             </Button>
        //             <Button danger icon={<DeleteOutlined />}>
        //                 Delete
        //             </Button>
        //         </Space>
        //     ),
        // },
    ];

    return (
        <div className="pcu-padding-24">
            <div className="pcu-margin-bottom-16">
                <Breadcrumbs />
            </div>

            <div className="main-content-card pcu-bg-white pcu-radius-16 pcu-shadow-sm pcu-overflow-hidden">
                <div className="pcu-padding-x-24 pcu-padding-top-24 pcu-padding-bottom-0 pcu-border-bottom">
                    <div className="pcu-flex pcu-flex-between pcu-flex-center-y pcu-margin-bottom-16">
                        <h1 className="pcu-font-22 pcu-font-700 pcu-color-title pcu-margin-0">
                            Product Inventory Thresholds
                        </h1>
                        <PrimaryButton
                            icon={<PlusOutlined />}
                            text="Add Threshold"
                            onClick={handleAdd}
                        />
                    </div>
                </div>

                <div className="pcu-padding-24">
                    <FilterBar>
                        <Col xs={24} md={12}>
                            <SearchInput
                                label="Search Product or Category"
                                placeholder="Search..."
                                value={params.search}
                                onChange={(e) => handleSearch(e.target.value)}
                            />
                        </Col>
                        <Col xs={24} md={12} style={{ textAlign: 'right' }}>
                            <ResetButton
                                icon={<ReloadOutlined />}
                                onClick={() => setParams({ page: 1, limit: 10, search: "" })}
                                text="Reset Filters"
                            />
                        </Col>
                    </FilterBar>

                    <Table
                        className="custom-table table-container pcu-margin-top-16"
                        columns={columns}
                        dataSource={thresholds}
                        rowKey="_id"
                        loading={loading}
                        bordered
                        pagination={{
                            current: params.page,
                            pageSize: params.limit,
                            total: total,
                            onShowSizeChange: (c, s) => setParams(prev => ({ ...prev, limit: s, page: 1 })),
                            onChange: (p) => setParams(prev => ({ ...prev, page: p })),
                            position: ['bottomRight'],
                            showTotal: (total) => <span className="pagination-total">Total {total} items</span>
                        }}
                        locale={{
                            emptyText: (
                                <EmptyState
                                    title="No Inventory Thresholds"
                                    description={params.search ? "No thresholds match your search." : "No inventory thresholds defined."}
                                    gradient="blue"
                                    onAction={handleAdd}
                                    actionText="Add Threshold"
                                    icon="inbox"
                                />
                            )
                        }}
                    />
                </div>
            </div>

            <AddProductThreshold
                open={modalOpen}
                onClose={(shouldRefresh) => {
                    setModalOpen(false);
                    if (shouldRefresh) refresh();
                }}
                isEditing={isEditing}
                thresholdDetails={selectedThreshold}
            />
        </div>
    );
};

export default ProductInventoryThresholds;
